import {FC, useState, useEffect, useRef} from 'react'
import {createPortal} from 'react-dom'
import {useLocation, useNavigate} from 'react-router-dom'
import axios from 'axios'
import {useAuth} from '../../../../app/modules/auth/core/Auth'
import {getTaskAccountCounts} from '../../../../app/modules/admin/tickets/core/_requests'
import {setAccountStatus} from '../../../../app/modules/admin/accounts/core/_requests'

// Effective post-footer position of an account (mirrors AccountDetailPage).
const footerPos = (a: any): 'start' | 'end' | null =>
  (a?.dynamic_options?.post_footer_position as any) || (a?.dynamic_options?.post_footer_enabled ? 'end' : null)

const API_URL = process.env.REACT_APP_API_URL || ''

interface AdminNote {
  _id: string
  text: string
  admin?: { username: string; email: string }
  created_at: string
}

interface AccountResult {
  _id: string
  first_name: string
  last_name: string
  email: string
  username: string
  phone: string
  trial_period_end: string
  account_type: string
  profile_count: number
  active_profiles: number
  connected_profile_count?: number
  next_meeting: string | null
  next_meeting_type: string | null
  profile_ids: string[]
  campaign_count: number
  post_count: number
  group_count: number
  scheduled_today: number
  posted_today: number
  missed_today: number
  errors_today: number
  posted_total?: number
  has_stale_health: boolean
  is_yad2?: boolean
  publishing_profiles?: number
  advertising_off?: boolean
  dismissed_alerts?: {
    low_posts?: boolean
    low_scheduled?: boolean
    no_profiles?: boolean
    no_campaigns?: boolean
    no_groups?: boolean
  }
  created_at: string
  admin_notes?: AdminNote[]
  monthly_payment_amount?: number
  has_payment?: boolean
  occupation?: string
  library_occupation?: string | null
  library_occupation_other?: string | null
  watermark_enabled?: boolean
  has_virtual_number?: boolean
  refered_by_info?: { id: string; name: string; is_affiliate: boolean } | null
  customer_info?: {
    account_status?: string
    [key: string]: any
  }
  dynamic_options?: {
    post_footer_enabled?: boolean
    post_footer_position?: 'start' | 'end' | null
    [key: string]: any
  }
}

// Compact yad2-brand badge shown for accounts imported/synced from a yad2 agent.
const Yad2Badge: FC = () => (
  <span
    title="לקוח יד2"
    style={{
      display: 'inline-flex', alignItems: 'center', fontSize: '10px', fontWeight: 800,
      lineHeight: '16px', padding: '1px 6px', borderRadius: '4px', letterSpacing: '0.2px',
      background: '#ff6a00', color: '#fff', flexShrink: 0,
    }}
  >
    yad2
  </span>
)

const AdminAccountSwitcher: FC = () => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [allAccounts, setAllAccounts] = useState<AccountResult[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [switching, setSwitching] = useState<string | null>(null)
  const [limitedProfileIds, setLimitedProfileIds] = useState<Set<string>>(new Set())
  const [filterLimited, setFilterLimited] = useState(false)
  const [taskCounts, setTaskCounts] = useState<Record<string, number>>({})
  const [issueCounts, setIssueCounts] = useState<Record<string, number>>({})
  const [filterIssues, setFilterIssues] = useState(false)
  const [noteInputId, setNoteInputId] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDays, setBulkDays] = useState('7')
  const [bulkTargetDate, setBulkTargetDate] = useState('')
  const [bulkExtending, setBulkExtending] = useState(false)
  const [normalizingEod, setNormalizingEod] = useState(false)
  const [occupationEditId, setOccupationEditId] = useState<string | null>(null)
  const [customOccupationText, setCustomOccupationText] = useState('')
  const [activeTab, setActiveTab] = useState<'active' | 'recent_expired' | 'expired' | 'disabled'>('active')
  const [recentExpiredAccounts, setRecentExpiredAccounts] = useState<AccountResult[]>([])
  const [recentExpiredLoading, setRecentExpiredLoading] = useState(false)
  const [recentExpiredLoaded, setRecentExpiredLoaded] = useState(false)
  const [expiredAccounts, setExpiredAccounts] = useState<AccountResult[]>([])
  const [expiredLoading, setExpiredLoading] = useState(false)
  const [expiredLoaded, setExpiredLoaded] = useState(false)
  const [disabledAccounts, setDisabledAccounts] = useState<AccountResult[]>([])
  const [disabledLoading, setDisabledLoading] = useState(false)
  const [disabledLoaded, setDisabledLoaded] = useState(false)
  const [searchResults, setSearchResults] = useState<(AccountResult & {_search_status?: string})[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const searchTimerRef = useRef<any>(null)
  const [filterMissed, setFilterMissed] = useState(false)
  const [filterPosted, setFilterPosted] = useState(false)
  const [filterErrors, setFilterErrors] = useState(false)
  const [filterYad2, setFilterYad2] = useState(false)
  const [filterFooterOn, setFilterFooterOn] = useState(false)
  const [filterFooterOff, setFilterFooterOff] = useState(false)
  const [filterWatermarkOn, setFilterWatermarkOn] = useState(false)
  const [filterWatermarkOff, setFilterWatermarkOff] = useState(false)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterNoProfile, setFilterNoProfile] = useState(false)
  const [filterVirtualNumber, setFilterVirtualNumber] = useState(false)
  const [statusSavingId, setStatusSavingId] = useState<string | null>(null)
  const [adBusy, setAdBusy] = useState<string | null>(null)
  const [bulkAdBusy, setBulkAdBusy] = useState(false)
  const [resyncId, setResyncId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const portalRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const {currentUser} = useAuth()

  // Check if restore mode is active from URL
  const isRestoreMode = new URLSearchParams(location.search).get('restore') === '1'

  // Toggle restore mode
  const toggleRestoreMode = () => {
    const params = new URLSearchParams(location.search)
    if (isRestoreMode) {
      params.delete('restore')
    } else {
      params.set('restore', '1')
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
  }

  // Get admin auth headers
  const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('admin_auth_token')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  // Check if admin is logged in and load accounts on mount
  useEffect(() => {
    const init = async () => {
      try {
        const authResponse = await axios.get(`${API_URL}/admin/check-auth`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
        if (authResponse.data.loggedIn !== true) {
          setLoading(false)
          return
        }
        // Render the header bar (incl. the call button) immediately — don't make
        // it wait for the heavy account-list/stat fetches below.
        setIsAdmin(true)
        setLoading(false)

        // Account list: load the LIGHT list first (skips the heavy 370K-profile /
        // 13.6M-post stat aggregations) so the dropdown is usable almost instantly,
        // then load the FULL list to enrich it with stats/badges. If the full list
        // already arrived, don't let the (slower-to-matter) light response clobber it.
        let gotFull = false
        axios
          .get(`${API_URL}/admin/search-accounts`, {headers: getAuthHeaders(), withCredentials: true})
          .then((r) => {
            gotFull = true
            setAllAccounts(r.data.accounts || [])
          })
          .catch(() => {})
        axios
          .get(`${API_URL}/admin/search-accounts?light=1`, {headers: getAuthHeaders(), withCredentials: true})
          .then((r) => {
            if (!gotFull) setAllAccounts(r.data.accounts || [])
          })
          .catch(() => {})
        getTaskAccountCounts()
          .then((r) => setTaskCounts(r.data.counts || {}))
          .catch(() => {})
        axios
          .get(`${API_URL}/admin/issues/account-open-counts`, {headers: getAuthHeaders(), withCredentials: true})
          .then((r) => setIssueCounts(r.data.counts || {}))
          .catch(() => {})
        axios
          .get(`${API_URL}/admin/limited-profiles`, {headers: getAuthHeaders(), withCredentials: true})
          .then((r) => {
            if (Array.isArray(r.data)) setLimitedProfileIds(new Set(r.data))
          })
          .catch(() => {})
      } catch (error) {
        setIsAdmin(false)
        setLoading(false)
      }
    }
    init()
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        portalRef.current && !portalRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Switch tab and lazy-load data
  const switchTab = async (tab: 'active' | 'recent_expired' | 'expired' | 'disabled') => {
    setActiveTab(tab)
    if (tab === 'recent_expired' && !recentExpiredLoaded) {
      setRecentExpiredLoading(true)
      try {
        const res = await axios.get(`${API_URL}/admin/search-accounts?days_grace=7`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
        const all = res.data.accounts || []
        // Include accounts already expired OR expiring today (up to end-of-today, local).
        const endOfToday = new Date()
        endOfToday.setHours(23, 59, 59, 999)
        setRecentExpiredAccounts(all.filter((a: AccountResult) => new Date(a.trial_period_end) <= endOfToday))
        setRecentExpiredLoaded(true)
      } catch {}
      setRecentExpiredLoading(false)
    }
    if (tab === 'expired' && !expiredLoaded) {
      setExpiredLoading(true)
      try {
        const res = await axios.get(`${API_URL}/admin/search-accounts?expired_only=1`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
        setExpiredAccounts(res.data.accounts || [])
        setExpiredLoaded(true)
      } catch {}
      setExpiredLoading(false)
    }
    if (tab === 'disabled' && !disabledLoaded) {
      setDisabledLoading(true)
      try {
        const res = await axios.get(`${API_URL}/admin/search-accounts?disabled_only=1`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
        setDisabledAccounts(res.data.accounts || [])
        setDisabledLoaded(true)
      } catch {}
      setDisabledLoading(false)
    }
  }

  // Debounced DB search for expired/disabled accounts
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([])
      return
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true)
      try {
        const res = await axios.get(`${API_URL}/admin/search-accounts?search_all=1&q=${encodeURIComponent(searchQuery)}&limit=20`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
        const dbAccounts: AccountResult[] = res.data.accounts || []
        const activeIds = new Set(allAccounts.map(a => a._id))
        // Only keep accounts not already in the active list
        const extra = dbAccounts.filter(a => !activeIds.has(a._id)).map(a => {
          const isExpired = new Date(a.trial_period_end) < new Date()
          const isDisabled = (a as any).is_active === false || (a as any).is_locked === true
          return {
            ...a,
            _search_status: isDisabled ? 'disabled' : isExpired ? 'expired' : ''
          }
        }).filter(a => a._search_status) // only show expired/disabled ones
        setSearchResults(extra)
      } catch {
        setSearchResults([])
      }
      setSearchLoading(false)
    }, 400)
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [searchQuery])

  // Check if account has any limited profiles
  const accountHasLimited = (account: AccountResult) => {
    if (!account.profile_ids || limitedProfileIds.size === 0) return false
    return account.profile_ids.some(id => limitedProfileIds.has(id))
  }

  // Set the admin customer-status label for an account, auto-saved on change.
  const setStatusForAccount = async (accountId: string, status: string) => {
    const apply = (a: AccountResult): AccountResult => a._id === accountId
      ? {...a, customer_info: {...(a.customer_info || {}), account_status: status}}
      : a
    setStatusSavingId(accountId)
    try {
      await setAccountStatus(accountId, status as any)
      setAllAccounts((p) => p.map(apply))
      setRecentExpiredAccounts((p) => p.map(apply))
      setExpiredAccounts((p) => p.map(apply))
      setDisabledAccounts((p) => p.map(apply))
      setSearchResults((p) => p.map(apply) as any)
    } catch (e: any) {
      alert('שגיאה בעדכון סטטוס לקוח: ' + (e?.response?.data?.error || e?.message || ''))
    } finally {
      setStatusSavingId(null)
    }
  }

  // Filter accounts based on search query and limited filter
  const sourceAccounts = activeTab === 'disabled' ? disabledAccounts : activeTab === 'expired' ? expiredAccounts : activeTab === 'recent_expired' ? recentExpiredAccounts : allAccounts
  const filteredAccounts = sourceAccounts.filter(account => {
    // Text search filter
    if (searchQuery.length >= 1) {
      const searchLower = searchQuery.toLowerCase()
      const fullName = `${account.first_name || ''} ${account.last_name || ''}`.toLowerCase()
      const matchesSearch = (
        fullName.includes(searchLower) ||
        (account.first_name || '').toLowerCase().includes(searchLower) ||
        (account.last_name || '').toLowerCase().includes(searchLower) ||
        (account.email || '').toLowerCase().includes(searchLower) ||
        (account.phone || '').toLowerCase().includes(searchLower)
      )
      if (!matchesSearch) return false
    }
    // Limited filter
    if (filterLimited && !accountHasLimited(account)) return false
    // yad2-only filter
    if (filterYad2 && !account.is_yad2) return false
    // Missed filter
    if (filterMissed && !(account.missed_today > 0)) return false
    // Posted filter
    if (filterPosted && !(account.posted_today > 0)) return false
    // Errors filter
    if (filterErrors && !(account.errors_today > 20)) return false
    // Post-footer on/off filter
    if (filterFooterOn && !footerPos(account)) return false
    if (filterFooterOff && footerPos(account)) return false
    // Watermark on/off filter
    if (filterWatermarkOn && !account.watermark_enabled) return false
    if (filterWatermarkOff && account.watermark_enabled) return false
    // Virtual-number filter
    if (filterVirtualNumber && !account.has_virtual_number) return false
    // Open-issues filter
    if (filterIssues && !(issueCounts[account._id] > 0)) return false
    // Customer-status filter
    if (filterStatus && (account.customer_info?.account_status || 'new_account') !== filterStatus) return false
    // No-connected-profile filter (account has no profile with a facebook_id)
    if (filterNoProfile && (account.connected_profile_count || 0) > 0) return false
    return true
  })

  const handleSwitchAccount = async (accountId: string) => {
    setSwitching(accountId)
    try {
      const response = await axios.get(`${API_URL}/admin/switch-account/${accountId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      })
      if (response.data.success && response.data.token) {
        // Use the auto-login route which handles token setup and redirect properly
        window.location.href = '/auth/auto-login?token=' + encodeURIComponent(response.data.token)
      } else {
        alert('Failed to switch account - no token received')
      }
    } catch (error) {
      console.error('Switch account error:', error)
      alert('Failed to switch account: ' + (error as any)?.response?.data?.error || 'Unknown error')
    } finally {
      setSwitching(null)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return 'Expired'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    return `${diffDays}d`
  }

  const getSeniority = (createdAt: string) => {
    if (!createdAt) return ''
    const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24))
    if (days < 30) return `${days} ימים`
    const months = Math.floor(days / 30)
    if (months < 12) return `${months} חודשים`
    const years = Math.floor(months / 12)
    const remainMonths = months % 12
    return remainMonths > 0 ? `${years} שנים ${remainMonths} חו'` : `${years} שנים`
  }

  const handleSaveNote = async (accountId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!noteText.trim()) return
    setNoteSaving(true)
    try {
      const res = await axios.post(`${API_URL}/admin/account-notes/${accountId}`, { text: noteText.trim() }, {
        headers: getAuthHeaders(), withCredentials: true
      })
      // Update account in state
      setAllAccounts(prev => prev.map(a => a._id === accountId ? { ...a, admin_notes: res.data.admin_notes } : a))
      setNoteText('')
      setNoteInputId(null)
    } catch (err) {
      alert('שגיאה בשמירת הערה')
    } finally {
      setNoteSaving(false)
    }
  }

  const handleSetOccupation = async (accountId: string, value: string, otherText?: string) => {
    try {
      await axios.post(`${API_URL}/admin/set-occupation/${accountId}`, {
        library_occupation: value,
        ...(otherText ? { library_occupation_other: otherText } : {})
      }, {
        headers: getAuthHeaders(), withCredentials: true
      })
      const occupationLabels: Record<string, string> = { real_estate: 'נדל"ן', cars: 'רכב', hr: 'כח אדם' }
      const occText = value === 'other' ? (otherText || 'אחר') : (occupationLabels[value] || value)
      setAllAccounts(prev => prev.map(a => a._id === accountId ? { ...a, occupation: occText, library_occupation: value, library_occupation_other: otherText || a.library_occupation_other } : a))
      setOccupationEditId(null)
      setCustomOccupationText('')
    } catch (err) {
      alert('שגיאה בשמירת תחום')
    }
  }

  // Patch an account across every list it might live in (active/expired/disabled/search).
  const patchAccount = (id: string, patch: Partial<AccountResult>) => {
    const upd = (arr: any[]) => arr.map(a => a._id === id ? { ...a, ...patch } : a)
    setAllAccounts(prev => upd(prev))
    setRecentExpiredAccounts(prev => upd(prev))
    setExpiredAccounts(prev => upd(prev))
    setDisabledAccounts(prev => upd(prev))
    setSearchResults(prev => upd(prev) as any)
  }

  // Toggle advertising for ONE account. Turning off also deletes today's unposted slots (server-side).
  const handleToggleAdvertising = async (account: AccountResult, e: React.MouseEvent) => {
    e.stopPropagation()
    const off = !account.advertising_off
    const who = `${account.first_name || ''} ${account.last_name || ''}`.trim() || account.email
    const msg = off
      ? `לכבות פרסום עבור ${who}?\nכל הפרופילים יסומנו כלא-מפרסמים והסלוטים שטרם פורסמו היום יימחקו.`
      : `להפעיל פרסום מחדש עבור ${who}?\nהפרופילים יופעלו וייווצרו סלוטים להיום.`
    if (!window.confirm(msg)) return
    setAdBusy(account._id)
    try {
      const res = await axios.post(`${API_URL}/admin/set-account-advertising/${account._id}`, { off }, { headers: getAuthHeaders(), withCredentials: true })
      patchAccount(account._id, { advertising_off: off, publishing_profiles: off ? 0 : (account.profile_count || 1) })
      if (!off) {
        alert(res.data?.scheduledSlots > 0
          ? `פרסום הופעל — נוצרו ${res.data.scheduledSlots} סלוטים להיום`
          : 'פרסום הופעל. לא נוצרו סלוטים להיום (ייתכן שבת / אין שעות זמינות / אין קמפיינים).')
      }
    } catch (err) {
      alert('שגיאה בשינוי הגדרת פרסום')
    } finally {
      setAdBusy(null)
    }
  }

  // Bulk turn advertising on/off for all selected accounts.
  const handleBulkAdvertising = async (off: boolean) => {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    const msg = off
      ? `לכבות פרסום עבור ${ids.length} חשבונות?\nהסלוטים שטרם פורסמו היום יימחקו.`
      : `להפעיל פרסום עבור ${ids.length} חשבונות?\nייווצרו סלוטים להיום עבור כל אחד.`
    if (!window.confirm(msg)) return
    setBulkAdBusy(true)
    try {
      const res = await axios.post(`${API_URL}/admin/bulk-set-advertising`, { account_ids: ids, off }, { headers: getAuthHeaders(), withCredentials: true })
      ids.forEach(id => patchAccount(id, { advertising_off: off, publishing_profiles: off ? 0 : undefined }))
      alert(`${res.data?.updated || ids.length} חשבונות עודכנו${off ? `, נמחקו ${res.data?.deletedSlots || 0} סלוטים` : `, נוצרו ${res.data?.scheduledSlots || 0} סלוטים`}`)
      setSelectedIds(new Set())
    } catch (err) {
      alert('שגיאה בשינוי פרסום בכמות')
    } finally {
      setBulkAdBusy(false)
    }
  }

  // Re-sync a yad2 customer's listings (works for any yad2 account — server
  // derives the agent phone from yad2_sync or the converting lead).
  const handleResyncYad2 = async (account: AccountResult, e: React.MouseEvent) => {
    e.stopPropagation()
    const who = `${account.first_name || ''} ${account.last_name || ''}`.trim() || account.email
    if (!window.confirm(`לסנכרן מחדש את נכסי ה-yad2 של ${who}?\nיעודכנו מחירים, יתווספו נכסים חדשים ויוסרו נכסים שירדו.\nפוסטים שהלקוח ערך ידנית יישארו ללא שינוי.`)) return
    setResyncId(account._id)
    try {
      const res = await axios.post(`${API_URL}/admin/resync-yad2/${account._id}`, {}, { headers: getAuthHeaders(), withCredentials: true })
      if (res.data?.success) {
        alert(`סנכרון הושלם ✓\nעודכנו ${res.data.updated || 0}, נוספו ${res.data.added || 0}, הוסרו ${res.data.removed || 0}.${res.data.skippedEdited ? `\n${res.data.skippedEdited} פוסטים שנערכו ידנית נשארו ללא שינוי.` : ''}${res.data.bootstrapped_from_lead ? '\n(טלפון הסוכן אותר מהליד)' : ''}`)
      } else {
        alert(res.data?.error || 'הסנכרון נכשל')
      }
    } catch (err: any) {
      alert('הסנכרון נכשל: ' + (err?.response?.data?.error || err?.message))
    } finally {
      setResyncId(null)
    }
  }

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAccounts.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredAccounts.map(a => a._id)))
    }
  }

  const handleBulkExtend = async (mode: 'days' | 'end_of_today' | 'end_of_tomorrow' | 'target_date' = 'days') => {
    const days = parseInt(bulkDays)
    if (selectedIds.size === 0) return
    if (mode === 'days' && !days) return
    if (mode === 'target_date' && !bulkTargetDate) return
    const label = mode === 'end_of_today' ? 'סוף היום'
      : mode === 'end_of_tomorrow' ? 'סוף מחר'
      : mode === 'target_date' ? `סוף ${bulkTargetDate}`
      : `${days} ימים`
    if (!window.confirm(`להאריך ${selectedIds.size} חשבונות עד ${label}?`)) return
    setBulkExtending(true)
    try {
      const payload: any = { account_ids: Array.from(selectedIds) }
      if (mode === 'end_of_today') payload.end_of_day_offset = 0
      else if (mode === 'end_of_tomorrow') payload.end_of_day_offset = 1
      else if (mode === 'target_date') payload.target_date = bulkTargetDate
      else payload.days = days
      const res = await axios.post(`${API_URL}/admin/bulk-extend-trial`, payload, { headers: getAuthHeaders(), withCredentials: true })
      if (res.data.success) {
        // Update local state
        setAllAccounts(prev => prev.map(a => {
          if (selectedIds.has(a._id)) {
            if (mode === 'end_of_today' || mode === 'end_of_tomorrow') {
              const target = new Date()
              if (mode === 'end_of_tomorrow') target.setDate(target.getDate() + 1)
              target.setHours(23, 59, 59, 999)
              return { ...a, trial_period_end: target.toISOString() }
            }
            if (mode === 'target_date') {
              // Parse YYYY-MM-DD as local-Jerusalem EOD; Date parses YYYY-MM-DD as UTC midnight,
              // so build the date explicitly to avoid timezone drift in the UI.
              const [y, m, d] = bulkTargetDate.split('-').map(Number)
              const target = new Date(y, m - 1, d, 23, 59, 59, 999)
              return { ...a, trial_period_end: target.toISOString() }
            }
            const currentEnd = new Date(a.trial_period_end)
            currentEnd.setDate(currentEnd.getDate() + days)
            return { ...a, trial_period_end: currentEnd.toISOString() }
          }
          return a
        }))
        setSelectedIds(new Set())
        alert(`${res.data.updated} חשבונות הוארכו בהצלחה`)
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'שגיאה בהארכה')
    } finally {
      setBulkExtending(false)
    }
  }

  const handleNormalizeEod = async () => {
    if (!window.confirm('לעדכן את כל תאריכי סיום התקופה לסוף היום (שעון ישראל)?')) return
    setNormalizingEod(true)
    try {
      const res = await axios.post(`${API_URL}/admin/normalize-trial-eod`, {}, { headers: getAuthHeaders(), withCredentials: true })
      if (res.data.success) {
        alert(`עודכנו ${res.data.normalized} חשבונות (${res.data.alreadyEod} כבר היו בסוף היום)`)
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'שגיאה בעדכון')
    } finally {
      setNormalizingEod(false)
    }
  }

  if (loading || !isAdmin) {
    return null
  }

  const accountId = (currentUser as any)?._id || (currentUser as any)?.id

  return (
    <div className="d-flex align-items-center gap-2">
      {/* Click to Call */}
      {(currentUser as any)?.phone && (
        <a
          href={`tel:${(currentUser as any).phone}`}
          className="d-flex align-items-center rounded px-3 py-2 bg-light-success text-decoration-none"
          style={{ border: '1px solid #50cd89', cursor: 'pointer' }}
          title={`Call ${(currentUser as any).phone}`}
        >
          <i className="bi bi-telephone-fill fs-5 me-2 text-success"></i>
          <span className="fw-bold fs-7 text-success">{(currentUser as any).phone}</span>
        </a>
      )}

      {/* Old CRM Link */}
      {accountId && (
        <a
          href={`https://s1.fbzipper.com/admin/customer/${accountId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="d-flex align-items-center rounded px-3 py-2 bg-light text-decoration-none"
          style={{ border: '1px solid #e4e6ef', cursor: 'pointer' }}
          title="Old CRM"
        >
          <i className="bi bi-box-arrow-up-right fs-5 me-2 text-muted"></i>
          <span className="fw-bold fs-7 text-muted">Old CRM</span>
        </a>
      )}

      {/* Restore Mode Toggle */}
      <div
        className={`d-flex align-items-center rounded px-3 py-2 ${isRestoreMode ? 'bg-light-warning' : 'bg-light'}`}
        style={{ cursor: 'pointer', border: isRestoreMode ? '1px solid #ffc107' : '1px solid #e4e6ef' }}
        onClick={toggleRestoreMode}
        title={isRestoreMode ? 'לחץ לכיבוי מצב שחזור' : 'לחץ להפעלת מצב שחזור'}
      >
        <i className={`bi bi-arrow-counterclockwise fs-5 me-2 ${isRestoreMode ? 'text-warning' : 'text-muted'}`}></i>
        <span className={`fw-bold fs-7 ${isRestoreMode ? 'text-warning' : 'text-muted'}`}>
          {isRestoreMode ? 'שחזור פעיל' : 'שחזור'}
        </span>
      </div>

      {/* Admin Mode Badge */}
      <div className="position-relative me-4" ref={dropdownRef}>
        <div
          className="d-flex align-items-center bg-light-danger rounded px-3 py-2"
          style={{ cursor: 'pointer' }}
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <i className="bi bi-person-badge text-danger fs-4 me-2"></i>
          <div className="d-flex flex-column">
            <span className="fw-bold text-danger fs-7">Admin Mode</span>
            <span className="text-muted fs-8">{allAccounts.length} active users</span>
          </div>
          <i className={`bi bi-chevron-${showDropdown ? 'up' : 'down'} text-danger ms-2`}></i>
        </div>

      {/* Dropdown */}
      {showDropdown && createPortal(
        <>
        <div
          className="position-fixed"
          style={{ top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 99999 }}
          onClick={() => setShowDropdown(false)}
        />
        <div
          ref={portalRef}
          className="position-fixed bg-white rounded shadow-lg"
          style={{
            width: '100vw',
            maxWidth: 'none',
            maxHeight: '80vh',
            zIndex: 100000,
            top: '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            border: '1px solid #e4e6ef'
          }}
        >
          {/* Search Header */}
          <div className="p-3 border-bottom bg-light">
            <div className="d-flex gap-2 align-items-center">
              <div className="position-relative flex-grow-1">
                <input
                  type="text"
                  className="form-control form-control-solid"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                <i
                  className="bi bi-search position-absolute text-muted"
                  style={{ right: '12px', top: '50%', transform: 'translateY(-50%)' }}
                />
              </div>
              <button
                className="btn btn-sm btn-light-primary"
                style={{ whiteSpace: 'nowrap', fontSize: '12px', height: '38px' }}
                onClick={(e) => { e.stopPropagation(); handleNormalizeEod() }}
                disabled={normalizingEod}
                title="עדכן את כל החשבונות כך שתאריך סיום התקופה יהיה בסוף היום (שעון ישראל)"
              >
                {normalizingEod ? '...' : 'תקן סיום תקופה לסוף יום'}
              </button>
            </div>
            <div className="mt-2 d-flex gap-2">
              {limitedProfileIds.size > 0 && (
                <span
                  className={`badge fs-8 cursor-pointer ${filterLimited ? 'badge-danger' : 'badge-light-danger'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setFilterLimited(!filterLimited)}
                >
                  <span style={{
                    display: 'inline-block', width: '6px', height: '6px',
                    borderRadius: '50%', background: '#f44336', marginLeft: '4px'
                  }}></span>
                  מוגבלים ({allAccounts.filter(a => accountHasLimited(a)).length})
                </span>
              )}
              <span
                className='badge fs-8 cursor-pointer'
                style={{ cursor: 'pointer', background: activeTab === 'recent_expired' ? '#ffc107' : '#fff8e1', color: activeTab === 'recent_expired' ? '#fff' : '#f57f17', border: '1px solid #ffc107' }}
                onClick={() => switchTab(activeTab === 'recent_expired' ? 'active' : 'recent_expired')}
              >
                {recentExpiredLoading ? (
                  <span className="spinner-border spinner-border-sm" style={{width: '10px', height: '10px', marginLeft: '4px'}}></span>
                ) : (
                  <span style={{
                    display: 'inline-block', width: '6px', height: '6px',
                    borderRadius: '50%', background: activeTab === 'recent_expired' ? '#fff' : '#ffc107', marginLeft: '4px'
                  }}></span>
                )}
                פג תוקף 7 ימים {recentExpiredLoaded ? `(${recentExpiredAccounts.length})` : ''}
              </span>
              <span
                className='badge fs-8 cursor-pointer'
                style={{ cursor: 'pointer', background: activeTab === 'expired' ? '#1e1e2d' : '#f5f5f5', color: activeTab === 'expired' ? '#fff' : '#1e1e2d', border: '1px solid #1e1e2d' }}
                onClick={() => switchTab(activeTab === 'expired' ? 'active' : 'expired')}
              >
                {expiredLoading ? (
                  <span className="spinner-border spinner-border-sm" style={{width: '10px', height: '10px', marginLeft: '4px'}}></span>
                ) : (
                  <span style={{
                    display: 'inline-block', width: '6px', height: '6px',
                    borderRadius: '50%', background: activeTab === 'expired' ? '#fff' : '#1e1e2d', marginLeft: '4px'
                  }}></span>
                )}
                כל פג תוקף {expiredLoaded ? `(${expiredAccounts.length})` : ''}
              </span>
              <span
                className='badge fs-8 cursor-pointer'
                style={{ cursor: 'pointer', background: activeTab === 'disabled' ? '#9e9e9e' : '#f5f5f5', color: activeTab === 'disabled' ? '#fff' : '#616161', border: '1px solid #9e9e9e' }}
                onClick={() => switchTab(activeTab === 'disabled' ? 'active' : 'disabled')}
              >
                {disabledLoading ? (
                  <span className="spinner-border spinner-border-sm" style={{width: '10px', height: '10px', marginLeft: '4px'}}></span>
                ) : (
                  <span style={{
                    display: 'inline-block', width: '6px', height: '6px',
                    borderRadius: '50%', background: activeTab === 'disabled' ? '#fff' : '#9e9e9e', marginLeft: '4px'
                  }}></span>
                )}
                מושבתים {disabledLoaded ? `(${disabledAccounts.length})` : ''}
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterMissed ? '#ff6b35' : '#fff0e8', color: filterMissed ? '#fff' : '#ff6b35', border: '1px solid #ff6b35' }}
                onClick={() => setFilterMissed(!filterMissed)}
              >
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px',
                  borderRadius: '50%', background: filterMissed ? '#fff' : '#ff6b35', marginLeft: '4px'
                }}></span>
                פספוסים ({allAccounts.filter(a => a.missed_today > 0 && new Date(a.trial_period_end) >= new Date()).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterPosted ? '#50cd89' : '#e6f9eb', color: filterPosted ? '#fff' : '#50cd89', border: '1px solid #50cd89' }}
                onClick={() => setFilterPosted(!filterPosted)}
              >
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px',
                  borderRadius: '50%', background: filterPosted ? '#fff' : '#50cd89', marginLeft: '4px'
                }}></span>
                פורסמו ({allAccounts.filter(a => a.posted_today > 0 && new Date(a.trial_period_end) >= new Date()).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterErrors ? '#f44336' : '#fdecea', color: filterErrors ? '#fff' : '#f44336', border: '1px solid #f44336' }}
                onClick={() => setFilterErrors(!filterErrors)}
              >
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px',
                  borderRadius: '50%', background: filterErrors ? '#fff' : '#f44336', marginLeft: '4px'
                }}></span>
                20+ שגיאות ({allAccounts.filter(a => a.errors_today > 20 && new Date(a.trial_period_end) >= new Date()).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterYad2 ? '#ff6a00' : '#fff1e6', color: filterYad2 ? '#fff' : '#ff6a00', border: '1px solid #ff6a00' }}
                onClick={() => setFilterYad2(!filterYad2)}
              >
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px',
                  borderRadius: '50%', background: filterYad2 ? '#fff' : '#ff6a00', marginLeft: '4px'
                }}></span>
                יד2 ({allAccounts.filter(a => a.is_yad2).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterFooterOn ? '#2b70fa' : '#e8f0ff', color: filterFooterOn ? '#fff' : '#2b70fa', border: '1px solid #2b70fa' }}
                onClick={() => { setFilterFooterOn(!filterFooterOn); if (!filterFooterOn) setFilterFooterOff(false) }}
              >
                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: filterFooterOn ? '#fff' : '#2b70fa', marginLeft: '4px' }}></span>
                פוטר פעיל ({allAccounts.filter(a => footerPos(a)).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterFooterOff ? '#6c757d' : '#eef0f2', color: filterFooterOff ? '#fff' : '#6c757d', border: '1px solid #6c757d' }}
                onClick={() => { setFilterFooterOff(!filterFooterOff); if (!filterFooterOff) setFilterFooterOn(false) }}
              >
                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: filterFooterOff ? '#fff' : '#6c757d', marginLeft: '4px' }}></span>
                פוטר כבוי ({allAccounts.filter(a => !footerPos(a)).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterWatermarkOn ? '#11998e' : '#e6f7f4', color: filterWatermarkOn ? '#fff' : '#11998e', border: '1px solid #11998e' }}
                onClick={() => { setFilterWatermarkOn(!filterWatermarkOn); if (!filterWatermarkOn) setFilterWatermarkOff(false) }}
              >
                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: filterWatermarkOn ? '#fff' : '#11998e', marginLeft: '4px' }}></span>
                חתימת לוגו פעיל ({allAccounts.filter(a => a.watermark_enabled).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterWatermarkOff ? '#6c757d' : '#eef0f2', color: filterWatermarkOff ? '#fff' : '#6c757d', border: '1px solid #6c757d' }}
                onClick={() => { setFilterWatermarkOff(!filterWatermarkOff); if (!filterWatermarkOff) setFilterWatermarkOn(false) }}
              >
                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: filterWatermarkOff ? '#fff' : '#6c757d', marginLeft: '4px' }}></span>
                חתימת לוגו כבוי ({allAccounts.filter(a => !a.watermark_enabled).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterIssues ? '#f44336' : '#fdecea', color: filterIssues ? '#fff' : '#f44336', border: '1px solid #f44336' }}
                onClick={() => setFilterIssues(!filterIssues)}
              >
                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: filterIssues ? '#fff' : '#f44336', marginLeft: '4px' }}></span>
                ⚠ תקלות פתוחות ({allAccounts.filter(a => issueCounts[a._id] > 0).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterNoProfile ? '#ffc107' : '#fff8e1', color: filterNoProfile ? '#fff' : '#8a6100', border: '1px solid #ffc107' }}
                onClick={() => setFilterNoProfile(!filterNoProfile)}
                title="חשבונות שלא חיברו אף פרופיל (אין פרופיל עם facebook_id)"
              >
                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: filterNoProfile ? '#fff' : '#ffc107', marginLeft: '4px' }}></span>
                🟡 לא חיברו פרופיל ({allAccounts.filter(a => !(a.connected_profile_count && a.connected_profile_count > 0)).length})
              </span>
              <span
                className={`badge fs-8 cursor-pointer`}
                style={{ cursor: 'pointer', background: filterVirtualNumber ? '#7c3aed' : '#f1e9ff', color: filterVirtualNumber ? '#fff' : '#7c3aed', border: '1px solid #7c3aed' }}
                onClick={() => setFilterVirtualNumber(!filterVirtualNumber)}
                title="חשבונות עם מספר וירטואלי פעיל"
              >
                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: filterVirtualNumber ? '#fff' : '#7c3aed', marginLeft: '4px' }}></span>
                📞 מספר וירטואלי ({allAccounts.filter(a => a.has_virtual_number).length})
              </span>
              <select
                title="סינון לפי סטטוס לקוח"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ fontSize: '10px', padding: '2px 4px', borderRadius: '4px', border: '1px solid #d6deea', background: filterStatus ? '#eef2ff' : '#fff', color: '#5a6a85', cursor: 'pointer' }}
              >
                <option value="">כל הסטטוסים</option>
                <option value="new_account">חדש ({allAccounts.filter(a => (a.customer_info?.account_status || 'new_account') === 'new_account').length})</option>
                <option value="working">לקוח עובד ({allAccounts.filter(a => a.customer_info?.account_status === 'working').length})</option>
                <option value="not_relevant">לא רלוונטי ({allAccounts.filter(a => a.customer_info?.account_status === 'not_relevant').length})</option>
              </select>
              {filteredAccounts.length > 0 && (() => {
                const cutoff = Date.now() - 24 * 60 * 60 * 1000
                const expiredTodayAccounts = activeTab === 'recent_expired'
                  ? filteredAccounts.filter(a => a.trial_period_end && new Date(a.trial_period_end).getTime() >= cutoff)
                  : []
                const unconnectedAccounts = filteredAccounts.filter(a => !(a.connected_profile_count && a.connected_profile_count > 0))
                const anyToggleFilter = filterLimited || filterMissed || filterPosted || filterErrors || filterYad2 || filterFooterOn || filterFooterOff || filterWatermarkOn || filterWatermarkOff || filterIssues || filterNoProfile || filterVirtualNumber
                return (
                  <div className="d-flex gap-1 ms-auto">
                    {anyToggleFilter && (
                      <span
                        className="badge fs-8 cursor-pointer badge-primary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedIds(new Set(filteredAccounts.map(a => a._id)))}
                        title="בחר את כל החשבונות שתואמים את הסינון הנוכחי"
                      >
                        בחר הכל מסונן ({filteredAccounts.length})
                      </span>
                    )}
                    {unconnectedAccounts.length > 0 && (
                      <span
                        className="badge fs-8 cursor-pointer badge-light-danger"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedIds(new Set(unconnectedAccounts.map(a => a._id)))}
                        title="חשבונות שאין להם אף פרופיל עם facebook_id"
                      >
                        בחר ללא פרופיל מחובר ({unconnectedAccounts.length})
                      </span>
                    )}
                    {activeTab !== 'active' && expiredTodayAccounts.length > 0 && (
                      <span
                        className="badge fs-8 cursor-pointer badge-light-warning"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedIds(new Set(expiredTodayAccounts.map(a => a._id)))}
                      >
                        בחר פג תוקף היום ({expiredTodayAccounts.length})
                      </span>
                    )}
                    {activeTab !== 'active' && (
                      <span
                        className="badge fs-8 cursor-pointer badge-light-primary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedIds(new Set(filteredAccounts.map(a => a._id)))}
                      >
                        בחר הכל פג תוקף ({filteredAccounts.length})
                      </span>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedIds.size > 0 && (
            <div className="p-2 border-bottom d-flex align-items-center gap-2" style={{background: '#e8f5e9'}}>
              <span className="fw-bold" style={{fontSize: '12px'}}>{selectedIds.size} נבחרו</span>
              <span style={{fontSize: '12px'}}>הארך ב-</span>
              <input
                type="number"
                value={bulkDays}
                onChange={e => setBulkDays(e.target.value)}
                style={{width: '60px', height: '28px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', textAlign: 'center'}}
                onClick={e => e.stopPropagation()}
              />
              <span style={{fontSize: '12px'}}>ימים</span>
              <button
                className="btn btn-sm btn-success py-0 px-3"
                style={{fontSize: '12px', height: '28px'}}
                onClick={(e) => { e.stopPropagation(); handleBulkExtend('days') }}
                disabled={bulkExtending}
              >
                {bulkExtending ? '...' : 'הארך'}
              </button>
              <button
                className="btn btn-sm btn-primary py-0 px-2"
                style={{fontSize: '12px', height: '28px'}}
                onClick={(e) => { e.stopPropagation(); handleBulkExtend('end_of_today') }}
                disabled={bulkExtending}
              >
                סוף היום
              </button>
              <button
                className="btn btn-sm btn-primary py-0 px-2"
                style={{fontSize: '12px', height: '28px'}}
                onClick={(e) => { e.stopPropagation(); handleBulkExtend('end_of_tomorrow') }}
                disabled={bulkExtending}
              >
                סוף מחר
              </button>
              <input
                type="date"
                value={bulkTargetDate}
                onChange={e => setBulkTargetDate(e.target.value)}
                onClick={e => e.stopPropagation()}
                style={{height: '28px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', padding: '0 6px'}}
              />
              <button
                className="btn btn-sm btn-primary py-0 px-2"
                style={{fontSize: '12px', height: '28px'}}
                onClick={(e) => { e.stopPropagation(); handleBulkExtend('target_date') }}
                disabled={bulkExtending || !bulkTargetDate}
              >
                קבע לסוף תאריך
              </button>
              <span style={{borderLeft: '1px solid #c8e6c9', height: '20px', margin: '0 4px'}} />
              <button
                className="btn btn-sm btn-danger py-0 px-2"
                style={{fontSize: '12px', height: '28px'}}
                onClick={(e) => { e.stopPropagation(); handleBulkAdvertising(true) }}
                disabled={bulkAdBusy}
                title="כבה פרסום לכל הנבחרים + מחק סלוטים שטרם פורסמו היום"
              >
                {bulkAdBusy ? '...' : '⏸ כבה פרסום'}
              </button>
              <button
                className="btn btn-sm btn-light-success py-0 px-2"
                style={{fontSize: '12px', height: '28px'}}
                onClick={(e) => { e.stopPropagation(); handleBulkAdvertising(false) }}
                disabled={bulkAdBusy}
                title="הפעל פרסום לכל הנבחרים"
              >
                ▶ הפעל
              </button>
              <button
                className="btn btn-sm btn-light py-0 px-2"
                style={{fontSize: '11px', height: '28px'}}
                onClick={(e) => { e.stopPropagation(); setSelectedIds(new Set()) }}
              >
                בטל
              </button>
              <span
                className="ms-auto text-primary"
                style={{fontSize: '11px', cursor: 'pointer'}}
                onClick={(e) => { e.stopPropagation(); toggleSelectAll() }}
              >
                {selectedIds.size === filteredAccounts.length ? 'בטל הכל' : 'בחר הכל'}
              </span>
            </div>
          )}

          {/* Accounts List */}
          <div style={{ maxHeight: 'calc(80vh - 80px)', overflowY: 'auto' }}>
            {(activeTab === 'expired' && expiredLoading) || (activeTab === 'recent_expired' && recentExpiredLoading) || (activeTab === 'disabled' && disabledLoading) ? (
              <div className="p-4 text-center">
                <div className="spinner-border text-primary" />
              </div>
            ) : filteredAccounts.length === 0 && searchResults.length === 0 ? (
              <div className="p-4 text-center text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                No accounts found
              </div>
            ) : (
              filteredAccounts.map((account, index) => (
                <div
                  key={account._id}
                  className={`px-3 py-2 d-flex align-items-center gap-3 ${
                    index !== filteredAccounts.length - 1 ? 'border-bottom' : ''
                  }`}
                  style={(() => {
                    const d = account.dismissed_alerts || {};
                    const alerts = [
                      !account.active_profiles && !d.no_profiles,
                      !account.campaign_count && !d.no_campaigns,
                      !account.post_count && !d.no_groups,
                      !account.group_count && !d.no_groups,
                      (account.post_count || 0) < 3 && !d.low_posts,
                      (account.scheduled_today || 0) < 100 && !d.low_scheduled,
                    ];
                    const isRed = alerts.some(Boolean);
                    return {
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      backgroundColor: isRed ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                      borderRight: isRed ? '3px solid #f44336' : 'none',
                    };
                  })()}
                  onClick={() => handleSwitchAccount(account._id)}
                  onMouseEnter={(e) => {
                    const d = account.dismissed_alerts || {};
                    const isRed = [
                      !account.active_profiles && !d.no_profiles,
                      !account.campaign_count && !d.no_campaigns,
                      !account.post_count && !d.no_groups,
                      !account.group_count && !d.no_groups,
                      (account.post_count || 0) < 3 && !d.low_posts,
                      (account.scheduled_today || 0) < 100 && !d.low_scheduled,
                    ].some(Boolean);
                    e.currentTarget.style.backgroundColor = isRed ? 'rgba(244, 67, 54, 0.1)' : '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    const d = account.dismissed_alerts || {};
                    const isRed = [
                      !account.active_profiles && !d.no_profiles,
                      !account.campaign_count && !d.no_campaigns,
                      !account.post_count && !d.no_groups,
                      !account.group_count && !d.no_groups,
                      (account.post_count || 0) < 3 && !d.low_posts,
                      (account.scheduled_today || 0) < 100 && !d.low_scheduled,
                    ].some(Boolean);
                    e.currentTarget.style.backgroundColor = isRed ? 'rgba(244, 67, 54, 0.05)' : 'transparent';
                  }}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedIds.has(account._id)}
                    onChange={() => {}}
                    onClick={(e) => toggleSelect(account._id, e)}
                    style={{width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer', accentColor: '#50cd89'}}
                  />
                  {/* Avatar - compact */}
                  <div className="position-relative" style={{flexShrink: 0}}>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '32px', height: '32px',
                        background: 'linear-gradient(135deg, #1877f2 0%, #0d65d9 100%)',
                        color: '#fff', fontWeight: 600, fontSize: '13px'
                      }}
                    >
                      {(account.first_name || account.email || '?').charAt(0).toUpperCase()}
                    </div>
                    {accountHasLimited(account) && (
                      <span style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: '#f44336', position: 'absolute', top: '-1px', right: '-1px',
                        border: '1.5px solid white'
                      }} title="יש פרופילים מוגבלים" />
                    )}
                    {account.has_stale_health && !accountHasLimited(account) && (
                      <span style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: '#ffc107', position: 'absolute', top: '-1px', right: '-1px',
                        border: '1.5px solid white'
                      }} title="פרופיל ללא בדיקת בריאות ב-10 דקות אחרונות" />
                    )}
                  </div>

                  {/* Name + email inline */}
                  <div style={{minWidth: '180px', maxWidth: '220px', flexShrink: 0}}>
                    <div className="fw-bold text-dark" style={{fontSize: '13px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                      {account.first_name} {account.last_name}
                      <span className="text-muted fw-normal ms-1" style={{fontSize: '10px'}}>
                        ({getSeniority(account.created_at)})
                      </span>
                    </div>
                    <div className="text-muted d-flex align-items-center" style={{fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                      {(account.occupation || account.library_occupation) ? (
                        <span
                          onClick={(e) => { e.stopPropagation(); setOccupationEditId(account._id); setCustomOccupationText(account.library_occupation === 'other' ? (account.library_occupation_other || '') : '') }}
                          style={{color: '#1b8a2d', background: '#e6f9eb', border: '1px solid #50cd89', borderRadius: '4px', padding: '0 5px', fontSize: '10px', fontWeight: 600, marginInlineEnd: '4px', cursor: 'pointer'}}
                        >
                          {account.occupation || (account.library_occupation === 'real_estate' ? 'נדל"ן' : account.library_occupation === 'cars' ? 'רכב' : account.library_occupation === 'hr' ? 'כח אדם' : account.library_occupation === 'other' ? (account.library_occupation_other || 'אחר') : account.library_occupation)}
                        </span>
                      ) : (
                        <span
                          onClick={(e) => { e.stopPropagation(); setOccupationEditId(account._id); setCustomOccupationText('') }}
                          style={{
                            fontSize: '10px', marginInlineEnd: '4px', cursor: 'pointer',
                            color: '#1877f2', background: '#e7f0fd', border: '1px solid #1877f2',
                            borderRadius: '4px', padding: '0 5px', fontWeight: 600
                          }}
                        >+ תחום</span>
                      )}
                      {account.email}
                    </div>
                    {account.refered_by_info && (
                      <div
                        title={`הובא ע"י ${account.refered_by_info.name}${account.refered_by_info.is_affiliate ? ' (שותף)' : ''}`}
                        style={{
                          fontSize: '10px', fontWeight: 600, marginTop: '2px',
                          color: '#7c3aed', background: '#f1e9ff', border: '1px solid #c9b3f5',
                          borderRadius: '4px', padding: '0 5px', display: 'inline-block',
                          maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        🤝 הובא ע"י {account.refered_by_info.name}{account.refered_by_info.is_affiliate ? ' (שותף)' : ''}
                      </div>
                    )}
                  </div>

                  {/* Dates compact */}
                  <div style={{fontSize: '10px', color: '#999', whiteSpace: 'nowrap', flexShrink: 0}}>
                    {account.created_at && (
                      <>
                        <span>{new Date(account.created_at).toLocaleDateString('he-IL')}</span>
                        <span className="mx-1">→</span>
                        <span>{new Date(account.trial_period_end).toLocaleDateString('he-IL')}</span>
                      </>
                    )}
                  </div>

                  {/* Badges inline */}
                  <div className="d-flex align-items-center gap-1 flex-wrap" style={{flexShrink: 0}}>
                    {account.is_yad2 && <Yad2Badge />}
                    {account.is_yad2 && (
                      <button
                        className="btn btn-sm btn-light-warning p-0 d-flex align-items-center justify-content-center"
                        style={{width: 22, height: 22, fontSize: '11px'}}
                        title="סנכרן מחדש נכסי yad2"
                        disabled={resyncId === account._id}
                        onClick={(e) => handleResyncYad2(account, e)}
                      >
                        {resyncId === account._id ? <span className="spinner-border spinner-border-sm" style={{width: '10px', height: '10px'}} /> : <i className="bi bi-arrow-repeat" />}
                      </button>
                    )}
                    {/* Advertising on/off indicator + shortcut toggle */}
                    {(account.profile_count || 0) > 0 && (
                      adBusy === account._id ? (
                        <span className="badge" style={{fontSize: '10px', padding: '2px 6px', background: '#eee', color: '#666'}}>
                          <span className="spinner-border spinner-border-sm" style={{width: '9px', height: '9px'}} />
                        </span>
                      ) : account.advertising_off ? (
                        <span
                          className="badge cursor-pointer"
                          style={{fontSize: '10px', padding: '2px 6px', cursor: 'pointer', background: '#fdecea', color: '#c62828', border: '1px solid #ef9a9a'}}
                          title="פרסום כבוי לכל הפרופילים — לחץ להפעלה מחדש"
                          onClick={(e) => handleToggleAdvertising(account, e)}
                        >
                          ⏸ פרסום כבוי
                        </span>
                      ) : (
                        <span
                          className="badge cursor-pointer"
                          style={{fontSize: '10px', padding: '2px 6px', cursor: 'pointer', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7'}}
                          title={`פרסום פעיל (${account.publishing_profiles ?? account.profile_count}/${account.profile_count}) — לחץ לכיבוי + מחיקת סלוטים שטרם פורסמו היום`}
                          onClick={(e) => handleToggleAdvertising(account, e)}
                        >
                          ▶ פרסום פעיל
                        </span>
                      )
                    )}
                    {/* Admin customer-status selector (auto-saves on change) */}
                    {(() => {
                      const status = account.customer_info?.account_status || 'new_account'
                      const busy = statusSavingId === account._id
                      return (
                        <select
                          value={status}
                          disabled={busy}
                          title="סטטוס לקוח"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {e.stopPropagation(); setStatusForAccount(account._id, e.target.value)}}
                          style={{fontSize: '10px', padding: '2px 4px', borderRadius: 4, border: '1px solid #d6deea', background: '#fff', color: '#5a6a85', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.5 : 1, marginInlineStart: 4}}
                        >
                          <option value="new_account">חדש</option>
                          <option value="working">לקוח עובד</option>
                          <option value="not_relevant">לא רלוונטי</option>
                        </select>
                      )
                    })()}
                    {account.account_type === 'demo_free' && (
                      <span className="badge badge-light-warning" style={{fontSize: '10px', padding: '2px 6px'}}>Demo</span>
                    )}
                    {account.next_meeting && (
                      <span className="badge badge-light-info" style={{fontSize: '10px', padding: '2px 6px'}} title={account.next_meeting_type || ''}>
                        {new Date(account.next_meeting).toLocaleDateString('he-IL', {day: '2-digit', month: '2-digit'})}
                        {' '}
                        {new Date(account.next_meeting).toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})}
                      </span>
                    )}
                    {taskCounts[account._id] > 0 && (
                      <span className="badge badge-light-warning" style={{fontSize: '10px', padding: '2px 6px'}}>
                        {taskCounts[account._id]} tasks
                      </span>
                    )}
                    {issueCounts[account._id] > 0 && (
                      <span className="badge badge-light-danger" style={{fontSize: '10px', padding: '2px 6px'}} title="תקלות פתוחות">
                        ⚠ {issueCounts[account._id]} תקלות
                      </span>
                    )}
                    <span className="badge badge-light-primary" style={{fontSize: '10px', padding: '2px 6px'}}>
                      {formatDate(account.trial_period_end)}
                    </span>
                    {(account.monthly_payment_amount || 0) > 0 && (
                      <span className="badge badge-light-success" style={{fontSize: '10px', padding: '2px 6px'}}>
                        ₪{account.monthly_payment_amount}
                      </span>
                    )}
                    {account.has_payment ? (
                      <span className="badge" style={{fontSize: '10px', padding: '2px 6px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7'}}>💳</span>
                    ) : (
                      <span className="badge" style={{fontSize: '10px', padding: '2px 6px', background: '#fce4ec', color: '#c62828', border: '1px solid #ef9a9a'}}>ללא כרטיס</span>
                    )}
                    {(account.posted_total || 0) > 800 ? (
                      <span
                        className="badge"
                        style={{fontSize: '10px', padding: '2px 6px', background: '#fff3cd', color: '#8a6100', border: '1px solid #ffd24d', fontWeight: 700}}
                        title={`פורסמו ${account.posted_total} פוסטים — מעל 800, גבה תשלום בתום הניסיון`}
                      >
                        💰 {account.posted_total}+
                      </span>
                    ) : (
                      <span
                        className="badge"
                        style={{fontSize: '10px', padding: '2px 6px', background: '#eef2f7', color: '#5a6a85', border: '1px solid #d6deea', fontWeight: 600}}
                        title={`פורסמו ${account.posted_total || 0} פוסטים בסך הכל`}
                      >
                        📤 {account.posted_total || 0}
                      </span>
                    )}
                  </div>

                  {/* Stats inline with per-field OK buttons */}
                  <div className="d-flex align-items-center gap-3" style={{fontSize: '11px', flexShrink: 0, marginRight: 'auto'}}>
                    {(() => {
                      const d = account.dismissed_alerts || {};
                      const stats = [
                        {label: 'פרופ׳', val: account.active_profiles, alertKey: 'no_profiles', isAlert: !account.active_profiles && !d.no_profiles},
                        {label: 'קמפ׳', val: account.campaign_count, alertKey: 'no_campaigns', isAlert: !account.campaign_count && !d.no_campaigns},
                        {label: 'פוסט', val: account.post_count, alertKey: 'low_posts', isAlert: (account.post_count || 0) < 3 && !d.low_posts},
                        {label: 'קבוצ׳', val: account.group_count, alertKey: 'no_groups', isAlert: !account.group_count && !d.no_groups},
                        {label: 'היום', val: account.scheduled_today, alertKey: 'low_scheduled', isAlert: (account.scheduled_today || 0) < 100 && !d.low_scheduled},
                        {label: 'פורסם', val: account.posted_today, alertKey: '', isAlert: false, color: '#50cd89'},
                        {label: 'פספוס', val: account.missed_today, alertKey: '', isAlert: false, color: account.missed_today > 0 ? '#ff6b35' : undefined},
                        {label: 'שגיאות', val: account.errors_today, alertKey: '', isAlert: false, color: account.errors_today > 0 ? '#f44336' : undefined},
                      ];
                      return stats.map((step) => (
                        <span
                          key={step.label}
                          className="d-flex align-items-center gap-1"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <span style={{fontWeight: 600, color: (step as any).color || (step.isAlert ? '#f44336' : step.val > 0 ? '#50cd89' : '#b5b5c3')}}>{step.val}</span>
                          <span style={{fontSize: '9px', color: '#999'}}>{step.label}</span>
                          {step.isAlert && (
                            <span
                              style={{
                                fontSize: '8px', color: '#4caf50', border: '1px solid #4caf50', borderRadius: '3px',
                                padding: '0 3px', cursor: 'pointer', lineHeight: '14px', fontWeight: 600
                              }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await axios.post(`${API_URL}/admin/dismiss-alert/${account._id}`, { field: step.alertKey }, {
                                    headers: getAuthHeaders(), withCredentials: true
                                  });
                                  setAllAccounts(prev => prev.map(a => a._id === account._id
                                    ? { ...a, dismissed_alerts: { ...a.dismissed_alerts, [step.alertKey]: true } }
                                    : a
                                  ));
                                } catch (err) { console.error('Error dismissing:', err); }
                              }}
                            >
                              OK
                            </span>
                          )}
                        </span>
                      ));
                    })()}
                  </div>

                  {/* Note inline */}
                  {(() => {
                    const lastNote = account.admin_notes?.length ? account.admin_notes[account.admin_notes.length - 1] : null
                    return lastNote ? (
                      <div
                        style={{fontSize: '10px', background: '#fff3cd', borderRadius: '4px', padding: '2px 6px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1}}
                        title={lastNote.text}
                        onClick={(e) => e.stopPropagation()}
                      >
                        📌 {lastNote.text}
                      </div>
                    ) : (
                      noteInputId === account._id ? (
                        <div className="d-flex gap-1" style={{flexShrink: 0}} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{fontSize: '10px', height: '24px', width: '120px'}}
                            placeholder="הערה..."
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNote(account._id, e as any) }}
                            autoFocus
                          />
                          <button
                            className="btn btn-sm btn-warning py-0 px-2"
                            style={{fontSize: '9px', height: '24px'}}
                            onClick={(e) => handleSaveNote(account._id, e)}
                            disabled={noteSaving || !noteText.trim()}
                          >
                            {noteSaving ? '...' : 'OK'}
                          </button>
                          <button
                            className="btn btn-sm btn-light py-0 px-1"
                            style={{fontSize: '9px', height: '24px'}}
                            onClick={(e) => { e.stopPropagation(); setNoteInputId(null); setNoteText('') }}
                          >
                            ×
                          </button>
                        </div>
                          ) : (
                            <span
                              className="text-primary"
                              style={{cursor: 'pointer', fontSize: '10px', flexShrink: 0}}
                              onClick={(e) => { e.stopPropagation(); setNoteInputId(account._id); setNoteText('') }}
                            >
                              + הערה
                            </span>
                          )
                    )
                  })()}

                  {/* Switch button */}
                  {switching === account._id ? (
                    <span className="spinner-border spinner-border-sm text-primary" style={{flexShrink: 0}} />
                  ) : (
                    <i className="bi bi-arrow-right-circle text-primary fs-5" style={{flexShrink: 0}}></i>
                  )}
                </div>
              ))
            )}
            {/* Search results from DB: expired/disabled accounts */}
            {searchQuery.length >= 2 && searchResults.length > 0 && (
              <>
                <div className="px-3 py-2 border-top" style={{background: '#f5f5f5'}}>
                  <span style={{fontSize: '11px', fontWeight: 600, color: '#7e8299'}}>
                    {searchLoading && <span className="spinner-border spinner-border-sm me-1" style={{width: '10px', height: '10px'}}></span>}
                    תוצאות נוספות מהמאגר ({searchResults.length})
                  </span>
                </div>
                {searchResults.map((account) => (
                  <div
                    key={account._id}
                    className="px-3 py-2 d-flex align-items-center gap-3 border-bottom"
                    style={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      opacity: 0.75,
                    }}
                    onClick={() => handleSwitchAccount(account._id)}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; e.currentTarget.style.opacity = '1' }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.02)'; e.currentTarget.style.opacity = '0.75' }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '32px', height: '32px', flexShrink: 0,
                        background: account._search_status === 'disabled' ? '#9e9e9e' : '#1e1e2d',
                        color: '#fff', fontWeight: 600, fontSize: '13px'
                      }}
                    >
                      {(account.first_name || account.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{minWidth: '180px', flexShrink: 0}}>
                      <div className="fw-bold text-dark d-flex align-items-center gap-1" style={{fontSize: '13px', lineHeight: 1.2}}>
                        <span>{account.first_name} {account.last_name}</span>
                        {account.is_yad2 && <Yad2Badge />}
                      </div>
                      <div className="text-muted" style={{fontSize: '11px'}}>{account.email}</div>
                    </div>
                    <span className="badge" style={{
                      fontSize: '10px', padding: '2px 8px',
                      background: account._search_status === 'disabled' ? '#e0e0e0' : '#1e1e2d',
                      color: account._search_status === 'disabled' ? '#616161' : '#fff',
                    }}>
                      {account._search_status === 'disabled' ? 'מושבת' : 'פג תוקף'}
                    </span>
                    <span style={{fontSize: '10px', color: '#999'}}>
                      {new Date(account.trial_period_end).toLocaleDateString('he-IL')}
                    </span>
                    {switching === account._id ? (
                      <span className="spinner-border spinner-border-sm text-primary ms-auto" style={{flexShrink: 0}} />
                    ) : (
                      <i className="bi bi-arrow-right-circle text-primary fs-5 ms-auto" style={{flexShrink: 0}}></i>
                    )}
                  </div>
                ))}
              </>
            )}
            {searchQuery.length >= 2 && searchLoading && searchResults.length === 0 && (
              <div className="px-3 py-2 border-top text-center" style={{background: '#f5f5f5'}}>
                <span className="spinner-border spinner-border-sm" style={{width: '12px', height: '12px'}}></span>
                <span className="ms-2" style={{fontSize: '11px', color: '#7e8299'}}>מחפש במאגר...</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-2 border-top bg-light text-center">
            <span className="text-muted fs-8">
              Showing {filteredAccounts.length}{searchResults.length > 0 ? ` + ${searchResults.length}` : ''} of {sourceAccounts.length} accounts
            </span>
          </div>
        </div>
        </>,
        document.body
      )}
      </div>

      {/* Occupation Popup */}
      {occupationEditId && createPortal(
        <>
          <div
            className="position-fixed"
            style={{ top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200000 }}
            onClick={() => { setOccupationEditId(null); setCustomOccupationText('') }}
          />
          <div
            className="position-fixed bg-white rounded shadow-lg p-4"
            style={{
              zIndex: 200001,
              top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: '340px'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h5 className="mb-3" style={{textAlign: 'right'}}>הגדרת תחום עיסוק</h5>
            <div className="d-flex flex-wrap gap-2 mb-3 justify-content-end">
              {[
                {val: 'real_estate', label: 'נדל"ן'},
                {val: 'cars', label: 'רכב'},
                {val: 'hr', label: 'כח אדם'},
              ].map(o => {
                const editAccount = allAccounts.find(a => a._id === occupationEditId)
                const isActive = editAccount?.library_occupation === o.val
                return (
                  <button
                    key={o.val}
                    className={`btn btn-sm ${isActive ? 'btn-success' : 'btn-outline-success'}`}
                    style={{fontSize: '13px', fontWeight: 600, minWidth: '70px'}}
                    onClick={() => handleSetOccupation(occupationEditId, o.val)}
                  >{o.label}</button>
                )
              })}
            </div>
            <div className="border-top pt-3" style={{textAlign: 'right'}}>
              <div className="fw-bold mb-2" style={{fontSize: '13px'}}>או הזן תחום מותאם אישית:</div>
              <input
                type="text"
                className="form-control mb-3"
                placeholder="לדוגמא: ביטוח, שיפוצים..."
                value={customOccupationText}
                onChange={e => setCustomOccupationText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && customOccupationText.trim()) handleSetOccupation(occupationEditId, 'other', customOccupationText.trim()) }}
                style={{textAlign: 'right'}}
              />
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => { setOccupationEditId(null); setCustomOccupationText('') }}
                >ביטול</button>
                <button
                  className="btn btn-sm btn-success"
                  disabled={!customOccupationText.trim()}
                  onClick={() => { if (customOccupationText.trim()) handleSetOccupation(occupationEditId, 'other', customOccupationText.trim()) }}
                >שמור</button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  )
}

export {AdminAccountSwitcher}
